import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, getStylesRef, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import staticURL from 'lib/staticURL';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const TEST_COMPONENT = 'OnboardingBanner';
const JELLYFISH_GIF_PATH = '/static/img/ai-chat-onboarding/onboarding-jellyfish.gif';

/** How far the jellyfish overflows above the card's top edge, in pixels. */
const JELLYFISH_OVERFLOW_PX = 56;
const JELLYFISH_HEIGHT_PX = 180;
/** Fixed height of the "Start personalizing" card, in pixels. */
const CARD_HEIGHT_PX = 144;

const useStyles = createStyles((theme) => ({
  // Leaves room above the card for the jellyfish to overflow without being clipped by the panel.
  root: {
    marginTop: JELLYFISH_OVERFLOW_PX,
    marginBottom: theme.spacing.xl,
  },
  // The card is itself the button: "Start personalizing" on the left, the jellyfish centered (and
  // overflowing the top edge), and an arrow on the right that nudges right on hover. The label and
  // arrow sit at the bottom of the card (the jellyfish occupies the space above them).
  card: {
    position: 'relative',
    overflow: 'visible',
    width: '100%',
    height: CARD_HEIGHT_PX,
    padding: theme.spacing.xl,
    borderRadius: theme.radius.lg,
    backgroundColor: theme.colors.blue[0],
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
    [`&:hover .${getStylesRef('arrow')}`]: {
      transform: 'translateX(4px)',
    },
  },
  startText: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 500,
    lineHeight: 1.2,
    color: theme.colors.blue[7],
    maxWidth: 110,
  },
  jellyfish: {
    position: 'absolute',
    left: '50%',
    top: -JELLYFISH_OVERFLOW_PX,
    transform: 'translateX(-50%)',
    height: JELLYFISH_HEIGHT_PX,
    width: 'auto',
    pointerEvents: 'none',
  },
  arrow: {
    ref: getStylesRef('arrow'),
    color: theme.colors.blue[7],
    fontSize: theme.fontSizes.xl,
    fontWeight: 500,
    transition: 'transform 150ms ease',
  },
  skip: {
    alignSelf: 'center',
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    '&:hover': {
      color: theme.colors.gray[8],
      textDecoration: 'underline',
    },
  },
}));

/**
 * The inline onboarding invite shown at the top of the welcome panel for users who haven't yet
 * onboarded. The whole lavender card (with its jellyfish illustration) is the "Start personalizing"
 * affordance that launches the onboarding survey; a quieter "Maybe later" below dismisses it. Because
 * it lives inside the welcome panel, onboarding stays optional — the user can ignore it and type a
 * chat directly.
 */
export function OnboardingBanner({onStart, onSkip}: {onStart: () => void; onSkip: () => void}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Stack
      spacing="sm"
      className={classes.root}
      {...getTestProps({component: TEST_COMPONENT, element: 'root'})}
    >
      <UnstyledButton
        aria-label="Start personalizing"
        className={classes.card}
        onClick={onStart}
        {...getTestProps({component: TEST_COMPONENT, element: 'start-button'})}
      >
        <Text className={classes.startText}>Start personalizing</Text>
        <img
          src={staticURL(JELLYFISH_GIF_PATH)}
          alt=""
          className={classes.jellyfish}
          {...getTestProps({component: TEST_COMPONENT, element: 'illustration'})}
        />
        <BenchlingIcon icon="arrow-right" aria-hidden={true} className={classes.arrow} />
      </UnstyledButton>
      <UnstyledButton
        className={classes.skip}
        onClick={onSkip}
        {...getTestProps({component: TEST_COMPONENT, element: 'skip-button'})}
      >
        Maybe later
      </UnstyledButton>
    </Stack>
  );
}
